import { AbortControllerManager } from '../abortControllerManager';
import { authClient } from '../client';
import {
  ISetLanguageRequest,
  IUserMeResponse,
  IUserData,
  IUploadProfilePictureResponse,
  IUpdateDosespotRequest,
  IPostDosespotSsoRequest,
  IPostDosespotSsoResponse,
  IUploadDocumentData,
  ISetLastUtilizedOrganizationRequest,
  IGetManualNotesResponse,
  ICreateManualNoteRequest,
  ICreateManualNoteResponse,
} from '@/types';
import { ITemplateResponse } from '@/types/common';

class UserApi extends AbortControllerManager {
  private USER_URL = '/v2/users';

  constructor() {
    super();
  }

  uploadProfilePicture(
    formData: FormData,
  ): Promise<IUploadProfilePictureResponse> {
    const url = `${this.USER_URL}/profile-picture`;
    const signal = this.createAbortController('uploadProfilePicture');
    return authClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
    });
  }

  uploadInsuranceCardPhoto(
    formData: FormData,
  ): Promise<ITemplateResponse<IUploadDocumentData>> {
    const url = `${this.USER_URL}/insurance-card-photo`;
    const signal = this.createAbortController('uploadInsuranceCardPhoto');
    return authClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
    });
  }

  uploadPhoneId(
    formData: FormData,
  ): Promise<ITemplateResponse<IUploadDocumentData>> {
    const url = `${this.USER_URL}/photo-id`;
    const signal = this.createAbortController('uploadPhoneId');
    return authClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal,
    });
  }

  getMe(): Promise<IUserMeResponse> {
    const url = `${this.USER_URL}/me`;
    const signal = this.createAbortController('getMe');
    return authClient.get(url, { signal });
  }

  setLanguage(data: ISetLanguageRequest) {
    const url = `${this.USER_URL}/language`;
    const signal = this.createAbortController('setLanguage');
    return authClient.post(url, data, { signal });
  }

  confirmAgreement() {
    const url = `${this.USER_URL}/eula-accept`;
    const signal = this.createAbortController('confirmAgreement');
    return authClient.post(url, undefined, { signal });
  }

  updateUserInfo(
    id: string,
    data: Partial<IUserData>,
  ): Promise<ITemplateResponse<IUserData>> {
    const url = `${this.USER_URL}/${id}`;
    const signal = this.createAbortController(`updateUserInfo-${id}`);
    return authClient.patch(url, data, { signal });
  }

  deleteMe() {
    const url = `${this.USER_URL}/me`;
    const signal = this.createAbortController('deleteMe');
    return authClient.delete(url, { signal });
  }

  getVisitNotesReport(
    id: string,
    type: 'visit-notes' | 'discharge-instructions',
    user_institution_role_id?: string,
  ) {
    let url = `${this.USER_URL}/${id}/${type}/pdf`;
    if (user_institution_role_id) {
      url = `${url}?filter[participant]=${user_institution_role_id}`;
    }
    const signal = this.createAbortController(
      `getVisitNotesReport-${id}-${type}-${user_institution_role_id ?? ''}`,
    );
    return authClient.get(url, {
      responseType: 'blob',
      signal,
    });
  }

  getManualNotes(
    id: string,
    user_institution_role_id: string,
  ): Promise<IGetManualNotesResponse> {
    const url = `${this.USER_URL}/${id}/manual-notes`;
    const signal = this.createAbortController(
      `getManualNotes-${id}-${user_institution_role_id}`,
    );
    return authClient.get(url, {
      params: { user_institution_role_id },
      signal,
    });
  }

  addManualNote(
    id: string,
    body: ICreateManualNoteRequest,
  ): Promise<ICreateManualNoteResponse> {
    const url = `${this.USER_URL}/${id}/manual-notes`;
    const signal = this.createAbortController(`addManualNote-${id}`);
    return authClient.post(url, body, { signal });
  }

  updateDosespot(
    body: IUpdateDosespotRequest,
  ): Promise<ITemplateResponse<IUserData>> {
    const url = `${this.USER_URL}/me/dosespot`;
    const signal = this.createAbortController(
      `updateDosespot-${body.institution_id}`,
    );
    return authClient.post(url, body, { signal });
  }

  postDosespotSso(
    body: IPostDosespotSsoRequest,
  ): Promise<ITemplateResponse<IPostDosespotSsoResponse>> {
    const url = `${this.USER_URL}/me/dosespot/sso`;
    const signal = this.createAbortController(
      `postDosespotSso-${body.user_institution_role_id}`,
    );
    return authClient.post(url, body, { signal });
  }

  setLastUtilizedOrganization(body: ISetLastUtilizedOrganizationRequest) {
    const url = `${this.USER_URL}/last-utilized-organization`;
    const signal = this.createAbortController('setLastUtilizedOrganization');
    return authClient.post(url, body, { signal });
  }
}

export const userApi = new UserApi();
